import { useState, type ReactNode } from 'react';
import type { IDockviewPanelProps } from 'dockview-react';
import { prefsStore, useSession, usePrefsValue } from '@adiox/ui';
import { INDICATORS } from '../../indicators/library';
import { normalizeTemplates } from '../../chart/templates';
import { useChartCommands } from '../../chart/chartCommands';
import { useWorkspace } from '../../workspace/workspaceStore';
import { ContextMenu, type MenuItem } from '../../chart/ChartOverlays';
import { useActiveLogin } from '../../trading/useTrading';
import { accountName, useAccountLabels } from '../useAccountLabels';
import { normalizeProfiles } from '../profiles';
import { WindowFrame } from './frames';

const TABS = [
  { id: 'common', label: 'Common' },
  { id: 'favorites', label: 'Favorites' },
] as const;
type Tab = (typeof TABS)[number]['id'];

const GROUPS = ['Trend', 'Oscillators', 'Volatility', 'Volumes', 'Bill Williams'] as const;

interface TreeNode {
  id: string;
  label: string;
  icon: 'root' | 'folder' | 'account' | 'indicator' | 'template' | 'profile';
  bold?: boolean;
  children?: TreeNode[];
  /** Double-click or Enter. */
  onOpen?: () => void;
  /** Right-click. */
  menu?: MenuItem[];
}

const ICON_COLOR: Record<TreeNode['icon'], string> = {
  root: '#1d6fd1',
  folder: '#a07a10',
  account: '#1a9b35',
  indicator: '#7a3fc0',
  template: '#1d6fd1',
  profile: '#a07a10',
};

function NodeIcon({ kind }: { kind: TreeNode['icon'] }) {
  const c = ICON_COLOR[kind];
  if (kind === 'folder' || kind === 'profile') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M1.5 4.5h5l1 1.5h7v7.5h-13z" fill="#f5d77a" stroke={c} />
      </svg>
    );
  }
  if (kind === 'account') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <circle cx="8" cy="5.5" r="3" fill={c} />
        <path d="M2.5 14c.5-3.5 3-5 5.5-5s5 1.5 5.5 5z" fill={c} />
      </svg>
    );
  }
  if (kind === 'indicator') {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M1.5 12c2-6 4-8 6-4s4 2 7-5" fill="none" stroke={c} strokeWidth="1.5" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1.5" y="2.5" width="13" height="11" fill="#fff" stroke={c} />
      <path d="M3.5 11l3-4 2.5 2 3.5-4.5" fill="none" stroke={c} />
    </svg>
  );
}

/**
 * The Navigator: the user's trading accounts (double-click to switch), the indicators (double-click to add one to the
 * active chart; right-click to star it), chart templates and profiles. Favorites lists the starred indicators.
 */
export function Navigator({ api }: IDockviewPanelProps) {
  const [tab, setTab] = useState<Tab>('common');
  const [open, setOpen] = useState<Set<string>>(() => new Set(['root', 'accounts', 'indicators']));
  const [selected, setSelected] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; title: string; items: MenuItem[] } | null>(null);
  const accounts = useSession().user?.accounts ?? [];
  const login = useActiveLogin();
  const labels = useAccountLabels();
  const favorites = usePrefsValue((s) => s.data.navigatorFavorites) ?? [];
  const templates = normalizeTemplates(usePrefsValue((s) => s.data.chartTemplates));
  const profiles = normalizeProfiles(usePrefsValue((s) => s.data.profiles));
  const chart = useChartCommands((s) => s.active);

  const star = (id: string) =>
    prefsStore.set(
      'navigatorFavorites',
      favorites.includes(id) ? favorites.filter((f) => f !== id) : [...favorites, id],
    );
  const indicatorNode = (def: (typeof INDICATORS)[number]): TreeNode => ({
    id: `ind-${def.id}`,
    label: def.name,
    icon: 'indicator',
    onOpen: chart ? () => chart.addIndicator(def.id) : undefined,
    menu: [
      { label: 'Attach to chart', disabled: !chart, onSelect: () => chart?.addIndicator(def.id) },
      {
        label: favorites.includes(def.id) ? 'Remove from Favorites' : 'Add to Favorites',
        onSelect: () => star(def.id),
      },
    ],
  });

  const common: TreeNode = {
    id: 'root',
    label: 'Adiox Terminal',
    icon: 'root',
    children: [
      {
        id: 'accounts',
        label: 'Accounts',
        icon: 'folder',
        children: [...accounts]
          .sort((a, b) => b - a)
          .map((a) => ({
            id: `acc-${a}`,
            label: accountName(a, labels.get(a)),
            icon: 'account' as const,
            bold: a === login,
            onOpen: () => prefsStore.set('activeLogin', a),
            menu: [
              { label: 'Switch to this account', onSelect: () => prefsStore.set('activeLogin', a) },
              {
                label: 'Open a demo account…',
                onSelect: () => useWorkspace.getState().actions?.openSettings?.(),
              },
            ],
          })),
      },
      {
        id: 'indicators',
        label: 'Indicators',
        icon: 'folder',
        children: GROUPS.map((g) => ({
          id: `grp-${g}`,
          label: g,
          icon: 'folder' as const,
          children: INDICATORS.filter((d) => d.group === g).map(indicatorNode),
        })).filter((g) => g.children.length > 0),
      },
      {
        id: 'templates',
        label: 'Chart templates',
        icon: 'folder',
        children: Object.keys(templates).map((name) => ({
          id: `tpl-${name}`,
          label: name,
          icon: 'template' as const,
          onOpen: chart ? () => chart.applyTemplate(name) : undefined,
        })),
      },
      {
        id: 'profiles',
        label: 'Profiles',
        icon: 'folder',
        children: Object.keys(profiles).map((name) => ({
          id: `prof-${name}`,
          label: name,
          icon: 'profile' as const,
          onOpen: () => useWorkspace.getState().actions?.applyProfile?.(name),
        })),
      },
    ],
  };
  const favoriteNodes = INDICATORS.filter((d) => favorites.includes(d.id)).map(indicatorNode);

  const toggle = (id: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const renderNode = (node: TreeNode, depth: number): ReactNode => {
    const expandable = node.children !== undefined;
    const expanded = expandable && open.has(node.id);
    return (
      <li
        key={node.id}
        role="treeitem"
        aria-expanded={expandable ? expanded : undefined}
        aria-selected={selected === node.id}
      >
        <div
          className={`classic-tree-row flex items-center gap-1 ${selected === node.id ? 'is-selected' : ''}`}
          style={{ paddingLeft: depth * 14 + 2 }}
          tabIndex={0}
          onClick={() => setSelected(node.id)}
          onDoubleClick={() => (expandable ? toggle(node.id) : node.onOpen?.())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (expandable) toggle(node.id);
              else node.onOpen?.();
            } else if (e.key === 'ArrowRight' && expandable && !expanded) toggle(node.id);
            else if (e.key === 'ArrowLeft' && expandable && expanded) toggle(node.id);
          }}
          onContextMenu={(e) => {
            if (!node.menu) return;
            e.preventDefault();
            setSelected(node.id);
            setMenu({ x: e.clientX, y: e.clientY, title: node.label, items: node.menu });
          }}
        >
          <span
            className="grid w-3 place-items-center text-[10px] text-muted"
            aria-hidden="true"
            onClick={(e) => {
              if (!expandable) return;
              e.stopPropagation();
              toggle(node.id);
            }}
          >
            {expandable && node.children!.length > 0 ? (expanded ? '−' : '+') : ''}
          </span>
          <NodeIcon kind={node.icon} />
          <span className={`truncate ${node.bold ? 'font-semibold' : ''}`}>{node.label}</span>
        </div>
        {expanded && node.children!.length > 0 && (
          <ul role="group">{node.children!.map((c) => renderNode(c, depth + 1))}</ul>
        )}
      </li>
    );
  };

  return (
    <WindowFrame
      title="Navigator"
      label="Navigator"
      onClose={() => api.close()}
      tabs={TABS}
      tab={tab}
      onTab={setTab}
    >
      <div className="h-full overflow-auto bg-panel py-0.5">
        {tab === 'common' ? (
          <ul role="tree" aria-label="Navigator">
            {renderNode(common, 0)}
          </ul>
        ) : favoriteNodes.length === 0 ? (
          <p className="p-2 text-muted">Right-click an indicator and choose “Add to Favorites”.</p>
        ) : (
          <ul role="tree" aria-label="Favorites">
            {favoriteNodes.map((n) => renderNode(n, 0))}
          </ul>
        )}
      </div>
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          title={menu.title}
          items={menu.items}
          onClose={() => setMenu(null)}
        />
      )}
    </WindowFrame>
  );
}
